/**
 * Admin Dashboard Charts Module
 * Chart.js factory and lifecycle manager. Provides getDefaultOptions(type) for bar/line/doughnut charts
 * with dark theme, responsive layout, and cached configs. Manages chart instances in this.charts registry.
 * Used by revenue/traffic/device/source charts in main tab and Pinterest/Facebook/Google metrics in ads tab.
 */

(function () {
  'use strict';

  window.AdminDashboardCharts = {
    // Reference to core module
    core: null,

    // Chart instances registry
    charts: {},

    /**
     * Initialize Charts module
     */
    init(coreModule) {
      this.core = coreModule;
      this.charts = this.core?.state?.charts || {};
    },

    /**
     * Get default chart options by type
     * Common configuration used across all charts
     */
    getDefaultOptions(type = 'bar') {
      if (!this._defaultOptionsCache) this._defaultOptionsCache = {};
      if (this._defaultOptionsCache[type]) return this._defaultOptionsCache[type];

      const baseOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: true,
            position: 'top',
            labels: {
              color: '#ffffff',
              font: {
                weight: 'bold',
                size: 12,
              },
              padding: 15,
            },
          },
          tooltip: {
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            titleColor: '#ffffff',
            bodyColor: '#ffffff',
            borderColor: 'rgba(255, 255, 255, 0.2)',
            borderWidth: 1,
            padding: 12,
            displayColors: true,
            callbacks: {},
          },
        },
      };

      // Type-specific options
      const typeOptions = {
        bar: {
          scales: {
            x: {
              grid: {
                color: 'rgba(255, 255, 255, 0.1)',
              },
              ticks: {
                color: 'rgba(255, 255, 255, 0.9)',
                font: {
                  weight: 'bold',
                  size: 12,
                },
              },
            },
            y: {
              beginAtZero: true,
              grid: {
                color: 'rgba(255, 255, 255, 0.1)',
              },
              ticks: {
                color: 'rgba(255, 255, 255, 0.9)',
                font: {
                  weight: 'bold',
                  size: 12,
                },
              },
            },
          },
        },
        line: {
          scales: {
            x: {
              grid: {
                color: 'rgba(255, 255, 255, 0.1)',
              },
              ticks: {
                color: 'rgba(255, 255, 255, 0.9)',
                font: {
                  size: 11,
                },
              },
            },
            y: {
              beginAtZero: true,
              grid: {
                color: 'rgba(255, 255, 255, 0.1)',
              },
              ticks: {
                color: 'rgba(255, 255, 255, 0.9)',
                font: {
                  size: 11,
                },
              },
            },
          },
          elements: {
            line: {
              tension: 0.4, // Smooth curves
            },
            point: {
              radius: 4,
              hoverRadius: 6,
            },
          },
        },
        doughnut: {
          cutout: '60%',
          plugins: {
            legend: {
              position: 'right',
            },
          },
        },
        pie: {
          plugins: {
            legend: {
              position: 'right',
            },
          },
        },
      };

      this._defaultOptionsCache[type] = this.deepMerge(baseOptions, typeOptions[type] || {});
      // Return a deep clone so callers cannot mutate the cached template
      return this.deepMerge({}, this._defaultOptionsCache[type]);
    },

    /**
     * Deep merge utility for options
     * Bug Fix 6: Use Object.assign for better performance (was O(n²) nested loop)
     */
    deepMerge(target, source) {
      // Fast path for non-objects
      if (!this.isObject(target) || !this.isObject(source)) {
        return this.isObject(source)
          ? { ...source }
          : this.isObject(target)
            ? { ...target }
            : source !== undefined
              ? source
              : target;
      }

      const output = { ...target };

      Object.keys(source).forEach((key) => {
        if (this.isObject(source[key])) {
          if (!(key in target)) {
            output[key] = source[key];
          } else {
            output[key] = this.deepMerge(target[key], source[key]);
          }
        } else {
          output[key] = source[key];
        }
      });

      return output;
    },

    isObject(item) {
      return item && typeof item === 'object' && !Array.isArray(item);
    },

    /**
     * Helper: Get canvas context by element ID
     * Centralizes canvas lookup and context retrieval with error handling
     *
     * @param {string} canvasId - DOM id of canvas element
     * @returns {CanvasRenderingContext2D|null} Canvas context or null if not found
     */
    getCanvasContext(canvasId) {
      const canvas = document.getElementById(canvasId);
      if (!canvas) return null;

      try {
        return canvas.getContext('2d');
      } catch (_e) {
        return null;
      }
    },

    /**
     * Helper: Safely destroy and remove a chart instance
     * Consolidated destroy-before-recreate pattern
     *
     * @param {string} chartId - Unique identifier for the chart
     */
    destroyChartIfExists(chartId) {
      if (this.charts[chartId]) {
        try {
          this.charts[chartId].destroy();
        } catch (_e) {
          // Chart.js destroy() may throw if the canvas context was already released; safe to ignore
        }
      }
    },

    /**
     * Create or update a chart
     * Main factory method that abstracts Chart.js lifecycle
     *
     * @param {string} chartId - Unique identifier for the chart
     * @param {string} canvasId - DOM id of canvas element
     * @param {string} type - Chart type (bar, line, doughnut, pie, etc.)
     * @param {object} data - Chart data (labels, datasets)
     * @param {object} customOptions - Custom options to merge with defaults
     * @returns {Chart|null} Chart instance or null if canvas not found
     */
    createOrUpdateChart(chartId, canvasId, type, data, customOptions = {}) {
      const ctx = this.getCanvasContext(canvasId);
      if (!ctx) {
        return null;
      }

      // Fast path: when the chart already exists and the type hasn't changed,
      // update data in-place instead of destroying and recreating. This avoids
      // the expensive canvas teardown + full re-render of options and scales.
      const existing = this.charts[chartId];
      if (existing && existing.config && existing.config.type === type) {
        try {
          existing.data.labels = data.labels;
          existing.data.datasets = data.datasets;
          existing.update('none'); // 'none' skips animation for instant data swap
          return existing;
        } catch (_e) {
          // If the fast-path update fails (e.g., canvas released), fall through
          // to the full destroy+create path below.
        }
      }

      // Destroy existing chart if it exists (slow path: type changed or fast path failed)
      this.destroyChartIfExists(chartId);

      // Get default options and merge with custom
      const defaultOptions = this.getDefaultOptions(type);
      const mergedOptions =
        Object.keys(customOptions).length > 0
          ? this.deepMerge(defaultOptions, customOptions)
          : defaultOptions;

      // Create new chart
      try {
        this.charts[chartId] = new Chart(ctx, {
          type,
          data,
          options: mergedOptions,
        });

        return this.charts[chartId];
      } catch (_error) {
        return null;
      }
    },

    /**
     * Update existing chart data
     * More efficient than destroying and recreating
     */
    updateChartData(chartId, newData) {
      const chart = this.charts[chartId];
      if (!chart) {
        return false;
      }

      try {
        // Update labels if provided
        if (newData.labels) {
          chart.data.labels = newData.labels;
        }

        // Update datasets if provided
        if (newData.datasets) {
          chart.data.datasets = newData.datasets;
        }

        chart.update();
        return true;
      } catch (_error) {
        return false;
      }
    },

    /**
     * Destroy a specific chart
     */
    destroyChart(chartId) {
      if (this.charts[chartId]) {
        try {
          this.charts[chartId].destroy();
          delete this.charts[chartId];
          return true;
        } catch (_error) {
          return false;
        }
      }
      return false;
    },

    /**
     * Destroy all charts
     */
    destroyAllCharts() {
      const chartIds = Object.keys(this.charts);
      chartIds.forEach((id) => this.destroyChart(id));
    },

    /**
     * Adjust canvas height based on data points
     * Common pattern for horizontal bar charts
     */
    adjustCanvasHeight(canvasId, dataPointCount, minHeight = 300, pixelsPerPoint = 50) {
      const canvas = document.getElementById(canvasId);
      if (!canvas) return;

      const container = canvas.parentElement;
      if (!container) return;

      if (dataPointCount > 8) {
        const newHeight = Math.max(minHeight, dataPointCount * pixelsPerPoint);
        container.style.height = `${newHeight}px`;
        canvas.style.height = `${newHeight}px`;
      } else {
        container.style.height = `${minHeight}px`;
        canvas.style.height = `${minHeight}px`;
      }
    },

    /**
     * Common color palettes for charts
     */
    colorPalettes: {
      primary: [
        'rgba(59, 130, 246, 0.8)', // Blue
        'rgba(34, 197, 94, 0.8)', // Green
        'rgba(251, 146, 60, 0.8)', // Orange
        'rgba(236, 72, 153, 0.8)', // Pink
        'rgba(168, 85, 247, 0.8)', // Purple
        'rgba(14, 165, 233, 0.8)', // Light Blue
        'rgba(234, 179, 8, 0.8)', // Yellow
        'rgba(239, 68, 68, 0.8)', // Red
      ],
      borders: [
        'rgba(59, 130, 246, 1)',
        'rgba(34, 197, 94, 1)',
        'rgba(251, 146, 60, 1)',
        'rgba(236, 72, 153, 1)',
        'rgba(168, 85, 247, 1)',
        'rgba(14, 165, 233, 1)',
        'rgba(234, 179, 8, 1)',
        'rgba(239, 68, 68, 1)',
      ],
      gradient: [
        'rgba(99, 102, 241, 0.8)', // Indigo
        'rgba(139, 92, 246, 0.8)', // Violet
        'rgba(236, 72, 153, 0.8)', // Pink
        'rgba(244, 114, 182, 0.8)', // Light Pink
      ],
    },

    /**
     * Get color from palette by index.
     * Handles negative, NaN, and out-of-range indices safely.
     *
     * @param {number} index - Color index (wraps around palette length)
     * @param {string} [palette='primary'] - Palette name
     * @returns {string} RGBA color string
     */
    getColor(index, palette = 'primary') {
      const colors = this.colorPalettes[palette] || this.colorPalettes.primary;
      const i = Number(index);
      // Guard: NaN → 0; negative → wrap to positive via modulo arithmetic
      const safeIndex = isNaN(i) ? 0 : ((i % colors.length) + colors.length) % colors.length;
      return colors[safeIndex];
    },

    /**
     * Get border color from palette by index.
     * Handles negative, NaN, and out-of-range indices safely.
     *
     * @param {number} index - Color index (wraps around palette length)
     * @param {string} [palette='borders'] - Palette name
     * @returns {string} RGBA color string
     */
    getBorderColor(index, palette = 'borders') {
      const colors = this.colorPalettes[palette] || this.colorPalettes.borders;
      const i = Number(index);
      const safeIndex = isNaN(i) ? 0 : ((i % colors.length) + colors.length) % colors.length;
      return colors[safeIndex];
    },

    /**
     * Create dataset with automatic color assignment
     */
    createDataset(label, data, index = 0, customOptions = {}) {
      return {
        label,
        data,
        backgroundColor: customOptions.backgroundColor || this.getColor(index),
        borderColor: customOptions.borderColor || this.getBorderColor(index),
        borderWidth: customOptions.borderWidth || 2,
        ...customOptions,
      };
    },

    /**
     * Format chart data helper
     * Transforms raw API data into Chart.js format
     *
     * @param {Array<Object>} rawData - Array of data objects
     * @param {string} [labelKey='label'] - Key to use for chart labels
     * @param {string} [valueKey='value'] - Key to use for chart values
     * @returns {{ labels: string[], datasets: Array<Object> }}
     */
    formatChartData(rawData, labelKey = 'label', valueKey = 'value') {
      if (!Array.isArray(rawData)) {
        return { labels: [], datasets: [] };
      }

      const labels = [];
      const values = [];
      for (const item of rawData) {
        // Guard: skip non-object items (null, primitive) that would throw on key access
        if (!item || typeof item !== 'object') {
          labels.push('');
          values.push(0);
          continue;
        }
        labels.push(item[labelKey] != null ? String(item[labelKey]) : '');
        const raw = parseFloat(item[valueKey]);
        values.push(isNaN(raw) ? 0 : raw);
      }

      return {
        labels,
        datasets: [
          {
            label: 'Value',
            data: values,
            backgroundColor: this.getColor(0),
            borderColor: this.getBorderColor(0),
            borderWidth: 2,
          },
        ],
      };
    },

    /**
     * Show chart loading state
     */
    showChartLoading(chartContainerId) {
      const container = document.getElementById(chartContainerId)?.parentElement;
      if (!container || container.querySelector('.chart-loading-overlay')) return;

      const overlay = document.createElement('div');
      overlay.className = 'chart-loading-overlay';
      overlay.style.cssText =
        'position: absolute; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 10;';
      const spinner = document.createElement('div');
      spinner.className = 'spinner';
      overlay.appendChild(spinner);
      container.style.position = 'relative';
      container.appendChild(overlay);
    },

    /**
     * Hide chart loading state
     */
    hideChartLoading(chartContainerId) {
      const container = document.getElementById(chartContainerId)?.parentElement;
      if (!container) return;

      const overlay = container.querySelector('.chart-loading-overlay');
      if (overlay) {
        overlay.remove();
      }
    },

    /**
     * Get chart instance by ID
     */
    getChart(chartId) {
      return this.charts[chartId] || null;
    },

    /**
     * Check if chart exists
     */
    hasChart(chartId) {
      return chartId in this.charts && this.charts[chartId] !== null;
    },

    /**
     * Get all chart IDs
     */
    getAllChartIds() {
      return Object.keys(this.charts);
    },

    /**
     * Common tooltip formatters
     * All formatters guard against null/undefined/NaN input to avoid "NaN" in chart tooltips.
     *
     * @type {Object.<string, function(number|string|null|undefined): string>}
     */
    tooltipFormatters: {
      currency: (value) => {
        const n = parseFloat(value);
        if (isNaN(n)) return '$0.00';
        return `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
      },
      percentage: (value) => {
        const n = parseFloat(value);
        return isNaN(n) ? '0.0%' : `${n.toFixed(1)}%`;
      },
      number: (value) => {
        const n = parseFloat(value);
        return isNaN(n) ? '0' : n.toLocaleString('en-US');
      },
      integer: (value) => {
        const n = Number(value);
        return isNaN(n) ? '0' : Math.round(n).toLocaleString('en-US');
      },
    },

    /**
     * Apply tooltip formatter to chart options
     */
    applyTooltipFormatter(options, formatterType = 'number') {
      const formatter = this.tooltipFormatters[formatterType];
      if (!formatter) return options;

      if (!options.plugins) options.plugins = {};
      if (!options.plugins.tooltip) options.plugins.tooltip = {};
      if (!options.plugins.tooltip.callbacks) options.plugins.tooltip.callbacks = {};

      options.plugins.tooltip.callbacks.label = function (context) {
        let label = context.dataset.label || '';
        if (label) {
          label += ': ';
        }
        label += formatter(context.parsed.y ?? context.parsed);
        return label;
      };

      return options;
    },
  };
})();
